import {
  asRaw,
  asStr,
  buildDadosPrincipais,
  buildObservacoes,
  fmtDate,
  statusLabel,
  statusTone,
  type CardFieldMap,
  type DetailFieldMap,
} from '@/modules/kanban';
import { SITUACAO_INPI_TONE } from './data';

const STATUS_JURIDICO = [
  { value: 'em_andamento', label: 'Em andamento' },
  { value: 'aguardando', label: 'Aguardando' },
  { value: 'concluido', label: 'Concluído' },
  { value: 'indeferido', label: 'Indeferido' },
  { value: 'arquivado', label: 'Arquivado' },
];

export const juridicoCardFieldMap: CardFieldMap = (card) => {
  const titulo = card.nome_marca ? String(card.nome_marca) : 'Sem nome';
  const cliente = card.cliente_nome ? String(card.cliente_nome) : '—';
  const titular = card.titular ? String(card.titular) : null;
  const subtitle =
    titular && titular !== cliente ? `${cliente} · titular: ${titular}` : cliente;

  const numero = card.numero_processo ? String(card.numero_processo) : null;
  const classe = card.classe_nice ? `Classe ${String(card.classe_nice)}` : null;
  const situacao = card.situacao_inpi ? String(card.situacao_inpi) : null;
  const statusRaw = card.status ? String(card.status) : null;
  const statusVal = statusLabel(statusRaw);

  return {
    title: titulo,
    subtitle,
    chips: [
      { label: '', value: statusVal, tone: statusTone(statusRaw) },
      { label: 'Processo', value: numero, tone: 'info' },
      { label: 'Classe', value: classe, tone: 'neutral' },
      {
        label: 'Situação',
        value: situacao,
        tone: situacao ? (SITUACAO_INPI_TONE[situacao] ?? 'neutral') : 'neutral',
      },
    ],
    meta: card.responsavel_nome ? String(card.responsavel_nome) : null,
  };
};

// Jurídico (INPI/marcas) tem colunas DB-driven sem fluxo claro de fases —
// o legado também é display-only. No v2 emitimos uma única seção
// "Dados do processo" com todos os campos editáveis. O resto do shell
// (Dados principais, Status no topo, Observações) vem dos helpers
// compartilhados.
export const juridicoDetailFieldMap: DetailFieldMap = (card) => {
  const c = card as Record<string, unknown>;
  const out: ReturnType<DetailFieldMap> = [];
  out.push(...buildDadosPrincipais(c));

  const SECTION = 'Dados do processo';
  const txt = (key: string, label: string) =>
    out.push({
      section: SECTION,
      label,
      value: asStr(c[key]) ?? '—',
      alwaysShow: true,
      field: key,
      type: 'text',
      rawValue: asRaw(c[key]),
    });
  const dt = (key: string, label: string) =>
    out.push({
      section: SECTION,
      label,
      value: fmtDate(c[key]) ?? '—',
      alwaysShow: true,
      field: key,
      type: 'date',
      rawValue: asRaw(c[key]),
    });

  txt('nome_marca', 'Nome da marca');
  txt('numero_processo', 'Nº processo INPI');
  txt('classe_nice', 'Classe NICE');
  txt('natureza', 'Natureza da marca');
  txt('apresentacao', 'Apresentação');
  txt('titular', 'Titular');
  txt('titular_cnpj_cpf', 'CNPJ/CPF do titular');
  txt('procurador', 'Procurador');
  txt('imagem_marca_url', 'URL da imagem da marca');
  txt('especificacao', 'Especificação');
  dt('data_deposito', 'Data depósito');
  dt('data_concessao', 'Data concessão');
  dt('data_vigencia', 'Data vigência');
  dt('data_publicacao', 'Data publicação');

  // Status como select editável + situação INPI (read-only — vem de
  // consulta automática no INPI).
  out.push({
    section: SECTION,
    label: 'Status',
    value: statusLabel(asStr(c.status)) ?? '—',
    alwaysShow: true,
    field: 'status',
    type: 'select',
    options: STATUS_JURIDICO,
    rawValue: asRaw(c.status),
  });
  if (asStr(c.situacao_inpi)) {
    out.push({
      section: SECTION,
      label: 'Situação INPI',
      value: String(c.situacao_inpi),
    });
  }
  if (asStr(c.inpi_consultado_em)) {
    out.push({
      section: SECTION,
      label: 'Última consulta INPI',
      value: fmtDate(c.inpi_consultado_em) ?? '—',
    });
  }

  out.push(...buildObservacoes(c));
  return out;
};
